import { List, ListItem, Typography, styled } from "@mui/material";
import { Stack } from "@mui/system";
import { useTranslation } from "react-i18next";
import { PhoneEnum, RelationShipEnum } from "../../common/enums";
import { LineOfBusinessEnum } from "../../constants/BusinessConstants";
import Icon from "../Icon/Icon";
import ItemLabel from "../ItemLabel/ItemLabel";
import Panel from "../Panel/Panel";
import StatusBadge from "../StatusBadge/StatusBadge";

const ContactListItem = styled(ListItem)(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing("tiny"),
  paddingTop: theme.spacing("md"),
  paddingBottom: theme.spacing("md"),
  paddingLeft: 0,
  paddingRight: 0,
  '&:last-child': {
    borderBottom: 'none',
    paddingBottom: 0,
  },
}));

const ContactHeaderRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing("xs"),
}));

const ContactsPanel = ({ patientContacts, hospiceBranchContact, providerContacts, technicianDetails }) => {
  const { t } = useTranslation();

  return (
    <Panel title={t("PatientsDetails.contacts")} variant="outlined">
      <List disablePadding>
        {patientContacts?.length > 0 &&
          patientContacts.map((contactData) => (
            <ContactListItem key={contactData.phone} divider>
              {contactData?.contact_name && (
                <ContactHeaderRow>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {contactData.contact_name}&nbsp;({RelationShipEnum[contactData?.relationship]})
                  </Typography>
                  {contactData.is_primary_contact && (
                    <StatusBadge status="Primary"/>
                  )}
                </ContactHeaderRow>
              )}
              <ItemLabel label="Phone" text={<>{contactData.phone.replace(/(\d{3})-?(\d{3})-?(\d{4})/, "($1) $2-$3")} <Icon icon={contactData.phone_type == 1 ? "fa-mobile-screen-button" : "fa-phone"} /> ({contactData.phone_type && contactData.phone_type != 0 ? PhoneEnum[contactData.phone_type] : PhoneEnum[2]})</>} />
              {contactData.extension && <ItemLabel label="Ext" text={contactData.extension} />}
              {contactData.email && <ItemLabel label="Email" text={contactData.email} />}
              {contactData.notes && <ItemLabel label="Notes" text={contactData.notes} />}
            </ContactListItem>
          ))}
        {hospiceBranchContact?.hospice_name && (
          <ContactListItem divider>
            <ContactHeaderRow>
              <Typography variant="subtitle1" fontWeight="medium">
                {hospiceBranchContact.hospice_name}
                {hospiceBranchContact.hospice_branch_name && (
                  <> ({hospiceBranchContact.hospice_branch_name})</>
                )}
              </Typography>
              <StatusBadge status="Hospice" label={LineOfBusinessEnum.hospiceToPace} />
            </ContactHeaderRow>
            {hospiceBranchContact.phone_number && (
              <ItemLabel label="Phone" text={<>{hospiceBranchContact.phone_number.replace(/(\d{3})-(\d{3})-(\d{4})/, "($1) $2-$3")} <Icon icon="fa-phone" /> (Landline){hospiceBranchContact.extension && ` (${hospiceBranchContact.extension})`}</>} />
            )}
            {hospiceBranchContact.after_hours_phone && (
              <ItemLabel label="After hours phone" text={<>{hospiceBranchContact.after_hours_phone.replace(/(\d{3})-(\d{3})-(\d{4})/, "($1) $2-$3")} <Icon icon="fa-phone" /> (Landline){hospiceBranchContact.extension && ` (${hospiceBranchContact.extension})`}</>} />
            )}
          </ContactListItem>
        )}
        {providerContacts?.provider_name && (
          <ContactListItem divider>
            <ContactHeaderRow>
              <Typography variant="subtitle1" fontWeight="medium">
                {providerContacts.provider_name}
                {providerContacts.provider_location && (
                  <> ({providerContacts.provider_location})</>
                )}
              </Typography>
              <StatusBadge status="Provider" />
            </ContactHeaderRow>
            {providerContacts.phone_number && (
              <ItemLabel label="Phone" text={<>{providerContacts.phone_number.replace(/(\d{3})-(\d{3})-(\d{4})/, "($1) $2-$3")} <Icon icon="fa-phone" /> (Landline){providerContacts.extension && ` (${providerContacts.extension})`}</>} />
            )}
            {providerContacts.after_hours_phone && (
              <ItemLabel label="After hours phone" text={<>{providerContacts.after_hours_phone.replace(/(\d{3})-(\d{3})-(\d{4})/, "($1) $2-$3")} <Icon icon="fa-phone" /> (Landline){providerContacts.extension && ` (${providerContacts.extension})`}</>} />
            )}
          </ContactListItem>
        )}
        {technicianDetails?.technician_name && (
          <ContactListItem>
            <ContactHeaderRow>
              <Typography variant="subtitle1" fontWeight="medium">
                {technicianDetails.technician_name}
              </Typography>
              <StatusBadge status="Technician" />
            </ContactHeaderRow>
          </ContactListItem>
        )}
      </List>
    </Panel>
  );
};

export default ContactsPanel;
